import { useState, useRef, useMemo } from 'react';
import {
  Box, Typography, TextField, Autocomplete, Chip, Tooltip, IconButton, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
} from '@mui/material';
import { Download, Refresh, Upload, ContentCopy, Check, ViewColumn } from '@mui/icons-material';
import {
  DataGridPremium, GridColDef, GridRenderCellParams, GridPaginationModel, GridColumnResizeParams, type GridColumnVisibilityModel,
} from '@mui/x-data-grid-premium';
import { useQuery } from '@tanstack/react-query';
import { trackingService, ConversionRecord } from '../../services/tracking.service';
import { trafficSourcesService } from '../../services/traffic-sources.service';
import ColumnsDialog from './ColumnsDialog';
import { useColumnPrefs } from '../../hooks/useColumnPrefs';
import { useColumnWidths } from '../../hooks/useColumnWidths';
import { downloadGridCsv } from '../../utils/csv-export';
import { hiddenFrom, parsePostbackLead, trafficSourceVarNames } from '../../utils/tracking-helpers';
import { formatLocal, dayRangeParams } from '../../utils/timezone';
import TimeCell from '../../components/TimeCell';
import { COUNTRIES_MAP } from '../../utils/countries';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { useReportDefaults } from '../../hooks/useReportDefaults';
import DateRangePicker from '../../components/DateRangePicker';
import TimezoneSelect from '../../components/TimezoneSelect';
import TagSelect from '../../components/TagSelect';
import EntityFilterPicker, { FilterEntry } from '../../components/EntityFilterPicker';
import { ENTITY_FILTER_ORDER } from '../../components/entity-fetchers';
import { useFilterParams } from '../../hooks/useFilterParams';

// Client-only lead columns parsed from the incoming postback URL. `key` matches
// the flattened `lead_*` field added to each row (also used for CSV export).
const LEAD_COLUMNS: Array<{ field: keyof ReturnType<typeof parsePostbackLead>; key: string; header: string; width: number }> = [
  { field: 'age', key: 'lead_age', header: 'Age', width: 70 },
  { field: 'country', key: 'lead_country', header: 'Lead Country', width: 100 },
  { field: 'homeOwnership', key: 'lead_homeOwnership', header: 'Home', width: 90 },
  { field: 'insured', key: 'lead_insured', header: 'Insured', width: 80 },
  { field: 'currentInsuranceCompany', key: 'lead_insuranceCompany', header: 'Insurance Co', width: 150 },
  { field: 'multiDriver', key: 'lead_multiDriver', header: 'Multi Driver', width: 100 },
  { field: 'multiVehicle', key: 'lead_multiVehicle', header: 'Multi Vehicle', width: 100 },
];

const POSTBACK_STATUS: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' }> = {
  reported: { label: 'Reported', color: 'success' },
  partial: { label: 'Partial', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
  unknown: { label: 'Unknown', color: 'info' },
  filtered: { label: 'Filtered', color: 'default' },
  disabled: { label: 'Postback Off', color: 'default' },
  none: { label: 'No Postback', color: 'default' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Compact time-to-convert duration, e.g. "45s", "12m 30s", "3h 07m", "2d 4h".
function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '-';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, '0')}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

const EVENT_OPTIONS = [
  { value: '', label: 'All events' },
  { value: 'purchase', label: 'Purchase' },
  { value: 'lead', label: 'Lead' },
  { value: 'conversion', label: 'Conversion' },
  { value: 'signup', label: 'Signup' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ConversionsPage() {
  // Report state persisted to the URL so a refresh restores the exact view.
  // Must match the report you drilled in from, or the same rows cover a different window.
  const reportDefaults = useReportDefaults();
  const [dateRange, setDateRange] = useUrlState<[Date | null, Date | null]>(
    'dates',
    reportDefaults.dateRange,
    codecs.dateRange,
  );
  const [filters, setFilters] = useUrlState<FilterEntry[]>('filters', [], codecs.json<FilterEntry[]>());
  const [pickedField, setPickedField] = useUrlState('ffield', ENTITY_FILTER_ORDER[0], codecs.str);
  const [eventType, setEventType] = useUrlState('event', '', codecs.str);
  const [dims, setDims] = useUrlState<Record<string, string>>('dims', {}, codecs.json<Record<string, string>>());
  const [tagIds, setTagIds] = useUrlState<string[]>('tags', [], codecs.json<string[]>());
  // One object so page+size update in a single URL write (avoids multi-setter clobber).
  const [pagination, setPagination] = useUrlState('page', { page: 0, pageSize: 100 }, codecs.pageModel);
  const { page, pageSize } = pagination;
  const [timezone, setTimezone] = useUrlState('tz', reportDefaults.timezone, codecs.num);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [outboundFor, setOutboundFor] = useState<ConversionRecord | null>(null);

  const { date_from, date_to } = dayRangeParams(dateRange);

  const { filterParams, filtersKey } = useFilterParams(filters);
  const dimsKey = JSON.stringify(dims);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['conversions-list', date_from, date_to, filtersKey, dimsKey, eventType, tagIds.join(','), timezone, page, pageSize],
    queryFn: () => trackingService.getConversions({
      date_from,
      date_to,
      timezone,
      ...filterParams,
      // '' is the real "(none)" bucket, so pass through anything defined.
      ...Object.fromEntries(Object.entries(dims).filter(([, v]) => v !== undefined)),
      ...(eventType && { event_type: eventType }),
      ...(tagIds.length && { tags: tagIds }),
      limit: pageSize,
      offset: page * pageSize,
    }),
    // Keep the old rows while the next page loads: a grid whose rowCount drops to 0
    // mid-fetch clamps itself back to page 0.
    placeholderData: (prev) => prev,
  });

  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);

  // v1-v10 real names when exactly one traffic source is filtered (task 3).
  const tsFilterValues = filters.filter(f => f.field === 'traffic_source_id').map(f => f.value);
  const activeTsId = tsFilterValues.length === 1 ? tsFilterValues[0] : '';
  const { data: activeTs } = useQuery({
    queryKey: ['conv-ts-vnames', activeTsId],
    queryFn: () => trafficSourcesService.getById(activeTsId),
    enabled: !!activeTsId,
  });
  const vNames = useMemo(() => trafficSourceVarNames(activeTs || null), [activeTs]);

  // Flatten calculated lead columns (parsed from the incoming postback) onto rows,
  // so both the grid and the CSV export can use them (task 2).
  const rows = useMemo(() => (data?.data || []).map((r) => {
    const lead = parsePostbackLead(r.postback_url);
    return {
      ...r,
      lead_age: lead.age, lead_country: lead.country, lead_homeOwnership: lead.homeOwnership,
      lead_insured: lead.insured, lead_insuranceCompany: lead.currentInsuranceCompany,
      lead_multiDriver: lead.multiDriver, lead_multiVehicle: lead.multiVehicle,
    };
  }), [data]);

  const convTypeChip = (type: string) => {
    const isRejection = type === 'rejection';
    return (
      <Chip
        label={type}
        size="small"
        variant="filled"
        color={isRejection ? 'error' : type === 'purchase' ? 'success' : type === 'lead' ? 'info' : 'default'}
        sx={{ fontWeight: 500, minWidth: 72 }}
      />
    );
  };

  const allColumns: GridColDef[] = useMemo(() => [
    {
      field: 'click_created_at', headerName: 'Click Date', width: 160,
      valueFormatter: (v) => v ? formatLocal(v as string) : '-',
      renderCell: (p) => <TimeCell value={p.value} tz={timezone} />,
    },
    {
      field: 'created_at', headerName: 'Conversion Date', width: 160,
      valueFormatter: (v) => formatLocal(v as string),
      renderCell: (p) => <TimeCell value={p.value} tz={timezone} />,
    },
    {
      // Time-to-convert: conversion timestamp minus originating click timestamp.
      // Both are UTC, so the difference is timezone-independent.
      field: 'ttc', headerName: 'Diff', width: 100,
      valueGetter: (_v, row: ConversionRecord) =>
        row.click_created_at ? new Date(row.created_at).getTime() - new Date(row.click_created_at).getTime() : null,
      valueFormatter: (v) => v == null ? '-' : formatDuration(v as number),
    },
    // Source hierarchy first (traffic source → flow → offer → network → landing page).
    { field: 'traffic_source_name', headerName: 'Traffic Source', width: 130, valueFormatter: (v) => v || '-' },
    { field: 'flow_name', headerName: 'Flow', width: 150, valueFormatter: (v) => v || '-' },
    { field: 'offer_name', headerName: 'Offer', width: 160, valueFormatter: (v) => v || '-' },
    { field: 'network_name', headerName: 'Network', width: 130, valueFormatter: (v) => v || '-' },
    { field: 'landing_page_name', headerName: 'Landing Page', width: 140, valueFormatter: (v) => v || '-' },
    // Event + money.
    {
      field: 'event_type', headerName: 'Type', width: 110,
      renderCell: (params: GridRenderCellParams) => <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>{convTypeChip(params.value || 'default')}</Box>,
    },
    {
      field: 'revenue', headerName: 'Revenue', width: 110, type: 'number', align: 'right', headerAlign: 'right',
      renderCell: (params: GridRenderCellParams) => {
        const val = Number(params.value || 0);
        const isRejection = params.row.event_type === 'rejection';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" component="span"
              sx={{ color: isRejection ? 'error.main' : val > 0 ? 'success.main' : 'text.primary', fontWeight: val !== 0 ? 600 : 400 }}>
              ${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Box>
        );
      },
    },
    { field: 'currency', headerName: 'Cur', width: 60 },
    // Geo + calculated (client-only) lead columns parsed from the incoming postback.
    // country is the visitor's geo ISO-3166 code → show the full name (fall back to the code).
    { field: 'country', headerName: 'Country', width: 90, valueFormatter: (v) => v ? (COUNTRIES_MAP[String(v).toUpperCase()] || String(v)) : '-' },
    ...LEAD_COLUMNS.map(({ key, header, width }): GridColDef => ({
      field: key, headerName: header, width, valueFormatter: (v) => v || '-',
    })),
    // Identifiers.
    {
      field: 'transaction_id', headerName: 'Transaction ID', width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value ? (
            <Tooltip title={params.value}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} noWrap>
                {params.value}
              </Typography>
            </Tooltip>
          ) : <Typography variant="body2" color="text.disabled">-</Typography>}
        </Box>
      ),
    },
    {
      field: 'click_id', headerName: 'Click ID', width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title="Click to copy">
            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', cursor: 'pointer' }} noWrap
              onClick={() => params.value && navigator.clipboard.writeText(params.value)}>
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    { field: 'external_id', headerName: 'External ID', width: 130, valueFormatter: (v) => v || '-' },
    { field: 'external_id_type', headerName: 'Source', width: 100, valueFormatter: (v) => v || '-' },
    { field: 'type', headerName: 'Conv Type', width: 90, valueFormatter: (v) => v || 'default' },
    {
      field: 'postback_status', headerName: 'Postbacks', width: 110, sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const meta = POSTBACK_STATUS[params.value as string];
        if (!meta) return <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>-</Box>;
        const counts = (params.row as ConversionRecord).postback_counts || {};
        const summary = Object.entries(counts).map(([k, n]) => `${n} ${k}`).join(', ');
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Tooltip title={summary ? `${summary} — click for detail` : 'click for detail'}>
              <Chip label={meta.label} size="small" color={meta.color} variant="outlined"
                onClick={() => setOutboundFor(params.row as ConversionRecord)}
                sx={{ cursor: 'pointer' }} />
            </Tooltip>
          </Box>
        );
      },
    },
    // v1-v10 — real names from the selected traffic source when available, prefixed
    // with the slot (Voluum style, "V2: Campaign ID") with the slot shown in gray (task 3).
    ...Array.from({ length: 10 }, (_, i) => i + 1).map((n): GridColDef => {
      const real = vNames[`v${n}`];
      const slot = `V${n}`;
      return {
        field: `v${n}`,
        headerName: real ? `${slot}: ${real}` : slot,
        width: real ? 150 : 90,
        valueFormatter: (v) => v || '-',
        renderHeader: real
          ? () => (
            <span><span style={{ color: '#9e9e9e', fontWeight: 400 }}>{slot}:</span> {real}</span>
          )
          : undefined,
      };
    }),
    // Raw postback URLs last.
    {
      field: 'postback_url', headerName: 'Incoming Postback', width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value ? (
            <Tooltip title={params.value}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }} noWrap>{params.value}</Typography>
            </Tooltip>
          ) : '-'}
        </Box>
      ),
    },
    { field: 'postback_ip', headerName: 'Postback IP', width: 120, valueFormatter: (v) => v || '-' },
  ], [vNames, timezone]);

  const [columnsOpen, setColumnsOpen] = useState(false);
  const catalogue = useMemo(
    () => allColumns.map((c) => ({ field: c.field, label: String(c.headerName || c.field), available: true })),
    [allColumns]);
  const catalogueOrder = useMemo(() => catalogue.map((c) => c.field), [catalogue]);
  const { columns: orderedColumns, visibility, save: saveColumns, order: colOrder, hidden: colHidden } =
    useColumnPrefs(allColumns, catalogueOrder, 'conversions');
  const { columns, remember: rememberWidth } = useColumnWidths('conversions', orderedColumns);
  const visibleColumns = useMemo(
    () => columns.filter((c) => visibility[c.field] !== false), [columns, visibility]);

  return (
    <Box>
      {/* Filters row */}
      <Box ref={filterRef} sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Autocomplete size="small" sx={{ width: 170 }}
          options={EVENT_OPTIONS} getOptionLabel={(o) => o.label}
          value={EVENT_OPTIONS.find(e => e.value === eventType) || EVENT_OPTIONS[0]}
          onChange={(_, v) => setEventType(v?.value || '')}
          disableClearable
          renderInput={(params) => <TextField {...params} label="Event type" />}
        />
        <EntityFilterPicker
          filters={filters} onFilters={setFilters}
          field={pickedField} onField={setPickedField}
        />

        <TagSelect value={tagIds} onChange={setTagIds} allowCreate={false} label="Tags" placeholder="All" sx={{ minWidth: 180, maxWidth: 300 }} />

        <Box sx={{ flex: 1 }} />

        <TimezoneSelect value={timezone} onChange={setTimezone} />
        <DateRangePicker value={dateRange} onChange={setDateRange} />

        <Tooltip title="Export CSV">
          <span>
            <IconButton size="small" disabled={!rows.length}
              onClick={() => downloadGridCsv(rows, visibleColumns, 'conversions-export.csv')}>
              <Download fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Button size="small" variant="outlined" startIcon={<Upload fontSize="small" />}
          onClick={() => setUploadOpen(true)} sx={{ textTransform: 'none' }}>
          Upload
        </Button>

        <Tooltip title="Columns">
          <IconButton size="small" onClick={() => setColumnsOpen(true)}><ViewColumn fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => refetch()}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

        {Object.keys(dims).length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">From report:</Typography>
            {Object.entries(dims).filter(([, v]) => v !== undefined).map(([k, v]) => (
              <Chip
                key={k}
                size="small"
                label={`${k.toUpperCase()}: ${v === '' ? '(none)' : v}`}
                onDelete={() => {
                  const next = { ...dims };
                  delete next[k];
                  setDims(next);
                }}
              />
            ))}
          </Box>
        )}

      {/* Data Grid */}
      <DataGridPremium
        rows={rows}
        getRowId={(row: ConversionRecord) => row.id || `${row.click_id}-${row.created_at}`}
        columns={columns}
        columnVisibilityModel={visibility}
        onColumnVisibilityModelChange={(m: GridColumnVisibilityModel) => saveColumns(colOrder, hiddenFrom(m))}
        onColumnWidthChange={(params: GridColumnResizeParams) => rememberWidth(params.colDef.field, params.width)}
        loading={isFetching}
        density="compact"
        rowCount={data?.total || 0}
        pagination
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model: GridPaginationModel) => setPagination({
          page: model.pageSize !== pageSize ? 0 : model.page,
          pageSize: model.pageSize,
        })}
        pageSizeOptions={[50, 100, 200, 400]}
        disableRowSelectionOnClick
        sx={{
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          height: gridHeight,
          '& .MuiDataGrid-row:nth-of-type(even)': {
            bgcolor: 'action.hover',
          },
          '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
        }}
      />

      <ColumnsDialog
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        catalogue={catalogue}
        order={colOrder}
        hidden={colHidden}
        onSave={saveColumns}
      />

      <UploadConversionsDialog open={uploadOpen} onClose={() => { setUploadOpen(false); refetch(); }} />
      <OutboundPostbacksDialog conversion={outboundFor} onClose={() => setOutboundFor(null)} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Upload Conversions Dialog
// ---------------------------------------------------------------------------

function UploadConversionsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [lines, setLines] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleUpload = async () => {
    setUploading(true);
    setResult(null);
    try {
      const res = await trackingService.uploadConversions(lines);
      setResult({ success: true, message: `${res.inserted} conversion(s) uploaded successfully` });
      setLines('');
    } catch (err: any) {
      setResult({ success: false, message: err.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const lineCount = lines.split('\n').filter(l => l.trim() && !l.trim().startsWith('#')).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Conversions</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste one conversion per line in CSV format. All click IDs are validated before inserting.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, fontFamily: 'monospace', fontSize: '0.8rem', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
          Format: click_id, payout, transaction_id, event_type, currency<br />
          Example: acgj2ofviongvv0uuv1dxcm2, 25.50, txn_001, purchase, USD<br />
          Example: acgjbbgq0aurr4sfc82dpndk, 0, , lead
        </Typography>
        <TextField
          multiline minRows={8} maxRows={20} fullWidth
          placeholder={'# Paste conversions here (lines starting with # are ignored)\nacgj2ofviongvv0uuv1dxcm2, 25.50, txn_001, purchase, USD'}
          value={lines}
          onChange={(e) => { setLines(e.target.value); setResult(null); }}
          sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.85rem' }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {lineCount} line(s) to upload
        </Typography>
        {result && (
          <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 1 }}>
            {result.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained" onClick={handleUpload}
          disabled={uploading || lineCount === 0}
          startIcon={uploading ? <CircularProgress size={16} /> : <Upload />}
        >
          {uploading ? 'Uploading...' : `Upload ${lineCount} Conversion(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RequestPayload({ raw, mono }: { raw: string; mono: Record<string, unknown> }) {
  const [copied, setCopied] = useState(false);
  const text = prettyJson(raw);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is blocked without a secure context; the payload is still selectable.
    }
  };

  return (
    <Box component="details" sx={{ mt: 0.75 }}>
      <Box component="summary" sx={{ cursor: 'pointer', fontSize: '0.7rem', color: 'text.secondary' }}>
        Request payload
      </Box>
      <Box sx={{ position: 'relative' }}>
        <Tooltip title={copied ? 'Copied' : 'Copy'}>
          <IconButton size="small" onClick={copy}
            sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'background.paper' }}>
            {copied ? <Check fontSize="inherit" /> : <ContentCopy fontSize="inherit" />}
          </IconButton>
        </Tooltip>
        <Box component="pre" sx={{
          ...mono, mt: 0.5, mb: 0, p: 1, pr: 5, borderRadius: 1,
          bgcolor: 'action.hover', maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap',
        }}>
          {text}
        </Box>
      </Box>
    </Box>
  );
}

function OutboundPostbacksDialog({ conversion, onClose }: {
  conversion: ConversionRecord | null;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['conversion-outbound', conversion?.id],
    queryFn: () => trackingService.getConversionOutbound(conversion!.id),
    enabled: !!conversion,
  });

  const mono = { fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all' as const };

  return (
    <Dialog open={!!conversion} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Outbound Postbacks
        {conversion && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', ...mono }}>
            {conversion.click_id}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <CircularProgress size={24} />}
        {error && <Alert severity="error">Could not load outbound postbacks.</Alert>}
        {data?.length === 0 && (
          <Alert severity="info">
            Nothing recorded — this conversion predates outbound postback logging.
          </Alert>
        )}
        {data?.map((r) => {
          const meta = POSTBACK_STATUS[r.status] || { label: r.status, color: 'default' as const };
          // Nothing was configured, so rendering an entry row would invent one.
          if (r.status === 'none') {
            return (
              <Box key={r.entry_index} sx={{ py: 1.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No postback was configured for this conversion&apos;s offer or traffic source, so nothing was sent.
                </Typography>
              </Box>
            );
          }
          return (
            <Box key={r.entry_index} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Chip label={meta.label} size="small" color={meta.color} variant="outlined" />
                <Typography variant="body2">
                  {r.postback_name || 'name not recorded'}{r.postback_type ? ` · ${r.postback_type}` : ''}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  entry {r.entry_index}
                  {r.http_status != null && ` · HTTP ${r.http_status}`}
                  {r.duration_ms != null && ` · ${r.duration_ms}ms`}
                </Typography>
              </Box>
              {r.out_url && <Typography sx={mono}>{r.out_url}</Typography>}
              {r.error && <Typography sx={{ ...mono, color: 'error.main', mt: 0.5 }}>{r.error}</Typography>}
              {r.response && (
                <Typography sx={{ ...mono, color: 'text.secondary', mt: 0.5 }}>{r.response}</Typography>
              )}
              {r.request && <RequestPayload raw={r.request} mono={mono} />}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
